import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, X, CheckSquare, Pencil, Trash2, Search, MessageSquare, Eye, Calendar, Flag, User, Building2 } from 'lucide-react';
import CommentsModal from '../components/CommentsModal';

const COLUMNS = [
  { key: 'todo', label: 'To Do', color: '#a0a0a0' },
  { key: 'in_progress', label: 'In Progress', color: '#eab308' },
  { key: 'review', label: 'Review', color: '#a855f7' },
  { key: 'done', label: 'Done', color: '#22c55e' },
];
const priorityColor = { low: 'badge-gray', medium: 'badge-blue', high: 'badge-yellow', urgent: 'badge-red' };

// ── Task Detail / View Modal ─────────────────────────────────────
const TaskViewModal = ({ task, onClose, onEdit, canManage }) => {
  const statusLabel = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title" style={{ fontSize: 16 }}>{task.title}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canManage && (
              <button className="btn btn-secondary btn-sm" onClick={() => { onClose(); onEdit(task); }}>
                <Pencil size={13} /> Edit
              </button>
            )}
            <button className="modal-close" onClick={onClose}><X size={20} /></button>
          </div>
        </div>
        <div className="modal-body">
          {/* Status & Priority */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <span className={`badge ${priorityColor[task.priority]}`}>{task.priority}</span>
            <span className="badge badge-gray">{statusLabel[task.status] || task.status}</span>
          </div>

          {/* Description */}
          {task.description ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16, padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              {task.description}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, fontStyle: 'italic' }}>No description provided.</div>
          )}

          {/* Details grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {task.client && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <Building2 size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-muted)' }}>Client:</span>
                <span style={{ fontWeight: 500 }}>{task.client.companyName}</span>
              </div>
            )}
            {task.assignee && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <User size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-muted)' }}>Assigned to:</span>
                <span style={{ fontWeight: 500 }}>{task.assignee.name}</span>
              </div>
            )}
            {task.dueDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <Calendar size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-muted)' }}>Due date:</span>
                <span style={{ fontWeight: 500 }}>{new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            )}
            {task.completedAt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <CheckSquare size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-muted)' }}>Completed:</span>
                <span style={{ fontWeight: 500, color: '#22c55e' }}>{new Date(task.completedAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Task Create/Edit Modal ───────────────────────────────────────
const TaskModal = ({ onClose, onSave, clients, users, initial }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? { ...initial, assignedTo: initial.assignedTo || '', dueDate: initial.dueDate ? initial.dueDate.split('T')[0] : '', clientId: initial.clientId || '' }
      : { title: '', description: '', priority: 'medium', status: 'todo', dueDate: '', clientId: '', assignedTo: '' }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      assignedTo: form.assignedTo === '' ? null : form.assignedTo,
      dueDate: form.dueDate === '' ? null : form.dueDate,
      clientId: form.clientId === '' ? null : form.clientId,
    };
    try {
      if (isEdit) await axios.put(`/api/tasks/${initial.id}`, payload);
      else await axios.post('/api/tasks', payload);
      onSave();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Task' : 'New Task'}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="input-group"><label className="input-label">Title *</label><input className="input" required value={form.title} onChange={e => set('title', e.target.value)} /></div>
            <div className="input-group"><label className="input-label">Client</label>
              <select className="input select" value={form.clientId} onChange={e => set('clientId', e.target.value)}>
                <option value="">No client (general task)</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.companyName} — {c.contactPerson}</option>)}
              </select>
            </div>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Priority</label>
                <select className="input select" value={form.priority} onChange={e => set('priority', e.target.value)}>
                  {['low','medium','high','urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="input-group"><label className="input-label">Status</label>
                <select className="input select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {['todo','in_progress','review','done'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Due Date</label><input type="date" className="input" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} /></div>
              <div className="input-group"><label className="input-label">Assign To</label>
                <select className="input select" value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                  <option value="">Unassigned</option>{users.filter(u => u.role !== 'client').map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
            </div>
            <div className="input-group"><label className="input-label">Description</label><textarea className="input" rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary">{isEdit ? 'Save Changes' : 'Create Task'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);        // edit/create modal
  const [viewModal, setViewModal] = useState(null); // view-only modal
  const [commentsModal, setCommentsModal] = useState(null);
  const [filterClient, setFilterClient] = useState('');
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const isClient = user?.role === 'client';

  const load = () => {
    Promise.all([
      axios.get('/api/tasks'),
      canManage ? axios.get('/api/clients') : Promise.resolve({ data: { data: [] } }),
      canManage ? axios.get('/api/users') : Promise.resolve({ data: { data: [] } }),
    ]).then(([t, c, u]) => {
      setTasks(t.data.data);
      setClients(c.data.data);
      setUsers(u.data.data);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (taskId, newStatus) => {
    if (isClient) return;
    try { await axios.put(`/api/tasks/${taskId}`, { status: newStatus }); load(); }
    catch { alert('Failed to update task'); }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try { await axios.delete(`/api/tasks/${task.id}`); load(); }
    catch { alert('Failed to delete task'); }
  };

  const handleDragStart = (e, taskId) => { e.dataTransfer.setData('taskId', taskId); e.currentTarget.classList.add('dragging'); };
  const handleDragEnd = (e) => { e.currentTarget.classList.remove('dragging'); };
  const handleDragOver = (e, colKey) => { e.preventDefault(); setDragOver(colKey); };
  const handleDrop = (e, colKey) => {
    e.preventDefault(); setDragOver(null);
    if (isClient) return;
    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== colKey) updateStatus(taskId, colKey);
  };

  const filtered = tasks
    .filter(t => !filterClient || t.clientId === filterClient)
    .filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.assignee?.name?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      {modal && (
        <TaskModal onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }}
          clients={clients} users={users} initial={modal.task || undefined} />
      )}
      {viewModal && (
        <TaskViewModal
          task={viewModal.task}
          onClose={() => setViewModal(null)}
          onEdit={(task) => setModal({ task })}
          canManage={canManage}
        />
      )}
      {commentsModal && (
        <CommentsModal task={commentsModal.task} onClose={() => setCommentsModal(null)} currentUser={user} />
      )}

      <div className="flex-between mb-24">
        <div><h1 style={{ fontSize: 20, fontWeight: 700 }}>Tasks</h1><p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{tasks.length} total</p></div>
        {canManage && <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> New Task</button>}
      </div>

      <div className="search-bar">
        <div style={{ position: 'relative', maxWidth: 280 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="input" style={{ paddingLeft: 36 }} placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {canManage && (
          <select className="input select" style={{ maxWidth: 240 }} value={filterClient} onChange={e => setFilterClient(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.companyName} — {c.contactPerson}</option>)}
          </select>
        )}
      </div>

      {!filtered.length ? <div className="empty-state"><CheckSquare /><p>No tasks found</p></div> : (
        <div className="kanban">
          {COLUMNS.map(col => {
            const colTasks = filtered.filter(t => t.status === col.key);
            return (
              <div key={col.key}
                className={`kanban-col${dragOver === col.key && !isClient ? ' drag-over' : ''}`}
                onDragOver={e => !isClient && handleDragOver(e, col.key)}
                onDragLeave={() => setDragOver(null)}
                onDrop={e => handleDrop(e, col.key)}>
                <div className="kanban-col-title">
                  <span style={{ color: col.color }}>{col.label}</span>
                  <span style={{ background: 'var(--bg-hover)', borderRadius: 12, padding: '1px 8px', fontSize: 12 }}>{colTasks.length}</span>
                </div>
                {colTasks.map(task => (
                  <div key={task.id} className="kanban-card"
                    draggable={!isClient}
                    onClick={() => setViewModal({ task })}
                    onDragStart={!isClient ? e => handleDragStart(e, task.id) : undefined}
                    onDragEnd={!isClient ? handleDragEnd : undefined}
                    style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                      <div className="kanban-card-title" style={{ flex: 1, marginBottom: 0 }}>{task.title}</div>
                      {canManage && (
                        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                          <button onClick={e => { e.stopPropagation(); setModal({ task }); }} title="Edit"
                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 4px' }}>
                            <Pencil size={12} />
                          </button>
                          <button onClick={e => { e.stopPropagation(); deleteTask(task); }} title="Delete"
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 4px' }}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                    {task.client && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                        {task.client.companyName}
                      </div>
                    )}
                    {task.description && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Eye size={10} /> {task.description}
                      </div>
                    )}
                    <div className="kanban-card-meta">
                      <span className={`badge ${priorityColor[task.priority]}`} style={{ fontSize: 11 }}>{task.priority}</span>
                      {task.assignee && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{task.assignee.name}</span>}
                    </div>
                    {task.dueDate && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Due: {new Date(task.dueDate).toLocaleDateString()}</div>}
                    <button onClick={e => { e.stopPropagation(); setCommentsModal({ task }); }}
                      style={{ marginTop: 8, background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', padding: '4px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center' }}>
                      <MessageSquare size={11} />
                      {task.comments?.length > 0 ? `${task.comments.length} comment${task.comments.length > 1 ? 's' : ''}` : 'Comment'}
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Tasks;
